import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import { StateController } from '../../helpers/state/state-management/state-controller';
import { TranslationElement } from '../../types';
import { addTranslation } from '../../helpers/api/organization-api';
import organizationInfo from '../../helpers/state/organization-info';
import topicData from '../../helpers/state/topic-data';
import translationData from '../../helpers/state/translation-data';

@customElement('translated-span')
export class TranslatedSpan extends LitElement {
  @state() refresh: boolean = false;
  @state() language: string = translationData.language;
  private originalText: string = '';
  private text: string = '';
  amplitude_callsite: string = 'Translated Span';

  private readonly bindTranslationState = new StateController(this, translationData);

  async firstUpdated() {
    this.originalText = this.textContent ?? '';
    await this.translateText();

    // Listen for language changes and update the translation when the language changes.
    translationData.onLanguageChanged(() => {
      if (this.originalText) {
        this.translateText().then(() => {
          // Handle any actions after translation here.
        }).catch(error => {
          // Handle any errors that occur during translation here.
          console.error('An error occurred while translating the text:', error);
        });
      }
    });

    translationData.onOriginalTextChanged(() => {
      // get new inner text and update the translation
      this.originalText = this.textContent ?? '';
      this.translateText().then(() => {
        // Handle any actions after translation here.
      }).catch(error => {
        // Handle any errors that occur during translation here.
        console.error('An error occurred while translating the text:', error);
      });
    }
    );
  }

  async refreshText() {
    this.originalText = this.textContent ?? '';
    await this.firstUpdated();
  }

  private async translateText() {
    this.text = await translationData.translatePhrase(this.originalText.trim());
    this.requestUpdate();
  }

  private async updateText() {
    // Pop up a dialog to allow the user to edit the text.
    const text_prompt = `Enter the new translation for: ${this.originalText}`;
    const newText = prompt(text_prompt, this.text);
    if (newText === null || newText === this.text) {
      return;
    }
    const translation_element: TranslationElement = {
      language: translationData.language,
      original_text: this.originalText,
      translated_text: newText ?? this.text,
    };

    await addTranslation(this.amplitude_callsite, organizationInfo.id?.$oid ?? '', translation_element);
    if (newText) {
      this.text = newText;
      this.requestUpdate();
    }
  }

  render() {
    const editable = translationData.isReady && translationData.liveEditMode && this.text !== this.originalText && topicData.isWriter;
    return html`<span>${this.text}
    ${editable ? html`<span class="edit-area" @click="${this.updateText}"><mwc-icon>edit</mwc-icon><mwc-icon>translate</mwc-icon></span>` : ''}
    </span>`;
  }

  static styles = css`

    mwc-icon {
      cursor: pointer;
      --mdc-icon-size: 8px;
    }

    .edit-area {
      display: inline;
      color: var(--main-featured-color);
      cursor: text;
    }
  `;
}